<template>
  <b-form>
    <b-form-group
      v-if="isExpense"
      label="Tipo de saida"
      label-for="operation-category"
    >
      <b-form-radio-group
        id="operation-category"
        v-model="form.category"
        :options="categoryOptions"
        buttons
        button-variant="outline-primary"
        size="sm"
      />
    </b-form-group>

    <b-alert v-if="isBankDeposit" show variant="info" class="py-2">
      <span class="fas fa-university mr-1" />
      Esta saida registra dinheiro retirado do kiosk para deposito no banco.
    </b-alert>

    <b-form-group :label="nameLabel" label-for="name">
      <b-form-input
        id="name"
        v-model="form.name"
        :placeholder="namePlaceholder"
        :state="validation.name"
      />
      <b-form-invalid-feedback v-if="$v.form.name.$invalid">
        Informe o nome da operacao
      </b-form-invalid-feedback>
    </b-form-group>

    <b-form-group label="Valor" label-for="amount" :class="validationClass">
      <b-input-group prepend="R$">
        <b-form-input
          id="amount"
          v-model="form.amount"
          v-money="{
            decimal: ',',
            thousands: '.',
            precision: 2
          }"
          :state="validation.amount"
        />
      </b-input-group>
      <b-form-invalid-feedback v-if="$v.form.amount.$invalid">
        Informe o valor da operacao
      </b-form-invalid-feedback>
    </b-form-group>

    <b-form-group
      v-if="showReceiptPhotoField"
      :label="receiptPhotoLabel"
      label-for="receipt-photo"
      :class="receiptValidationClass"
    >
      <b-alert v-if="receiptPhotoError" show variant="danger" class="py-2">
        <span class="fas fa-exclamation-triangle mr-1" />
        {{ receiptPhotoError }}
      </b-alert>

      <b-form-file
        id="receipt-photo"
        accept="image/*"
        browse-text="Escolher"
        :placeholder="receiptPhotoPlaceholder"
        :state="validation.receiptPhoto"
        @input="onReceiptPhotoInput"
      />
      <b-form-invalid-feedback v-if="$v.form.receiptPhoto.$invalid">
        {{ receiptPhotoInvalidMessage }}
      </b-form-invalid-feedback>

      <div class="receipt-actions mt-2">
        <b-button
          size="sm"
          variant="outline-primary"
          @click="startReceiptCamera"
        >
          <span class="fas fa-camera mr-1" />
          {{ receiptCameraActive ? 'Reiniciar camera' : 'Tirar foto' }}
        </b-button>

        <b-button
          v-if="receiptCameraActive"
          size="sm"
          variant="primary"
          @click="captureReceiptPhoto"
        >
          <span class="fas fa-dot-circle mr-1" />
          Capturar
        </b-button>
      </div>

      <div v-if="receiptCameraActive" class="receipt-camera mt-2">
        <video
          ref="receiptVideo"
          class="receipt-camera-video"
          autoplay
          muted
          playsinline
        />
      </div>

      <div v-else-if="receiptPreviewUrl" class="receipt-preview mt-2">
        <img :src="receiptPreviewUrl" :alt="receiptPhotoAlt" />
      </div>
      <small v-else-if="form.receiptPhotoUrl" class="text-muted d-block mt-2">
        Comprovante atual mantido. Envie outra imagem apenas se quiser
        substituir.
      </small>
      <small
        v-else-if="!isReceiptPhotoRequired"
        class="text-muted d-block mt-2"
      >
        {{ receiptPhotoHelp }}
      </small>
    </b-form-group>
  </b-form>
</template>

<script>
import withFormValidation from '@/mixins/withFormValidation'
import { required } from 'vuelidate/lib/validators'
import { formatCurrency, parseCurrency } from '@/helpers/numbers'
import { OPERATION_CATEGORIES, OPERATION_TYPES } from '@/constants'
import { mapGetters } from 'vuex'

export default {
  name: 'OperationForm',
  filters: { formatCurrency },
  mixins: [
    withFormValidation({
      fields: ['name', 'amount', 'receiptPhoto']
    })
  ],
  validations: {
    form: {
      name: {
        required(value) {
          return this.isBankDeposit || required(value)
        }
      },
      amount: {
        required: (value) => parseCurrency(value) > 0
      },
      receiptPhoto: {
        required(value) {
          if (!this.showReceiptPhotoField) return true
          if (
            this.isReceiptPhotoRequired &&
            !value &&
            !this.form.receiptPhotoUrl
          ) {
            return false
          }

          return !value || (value.type && value.type.startsWith('image/'))
        }
      }
    }
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    depositPhotoSettings: {
      type: Object,
      default: null
    },
    type: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      receiptPreviewUrl: null,
      receiptPhotoError: null,
      receiptCameraActive: false,
      receiptCameraStream: null
    }
  },
  computed: {
    ...mapGetters('store', {
      storeHasDepositPhotos: 'hasDepositPhotos',
      storeRequiresDepositPhoto: 'isDepositPhotoRequired'
    }),
    form: {
      set(value) {
        this.$emit('input', value)
      },
      get() {
        return this.value
      }
    },
    isExpense() {
      return this.type === OPERATION_TYPES.EXPENSE
    },
    isBankDeposit() {
      return this.form.category === OPERATION_CATEGORIES.BANK_DEPOSIT
    },
    showReceiptPhotoField() {
      return this.isExpense
    },
    isReceiptPhotoRequired() {
      const hasDepositPhotos = this.depositPhotoSettings
        ? this.depositPhotoSettings.hasDepositPhotos
        : this.storeHasDepositPhotos
      const isDepositPhotoRequired = this.depositPhotoSettings
        ? this.depositPhotoSettings.isDepositPhotoRequired
        : this.storeRequiresDepositPhoto

      return Boolean(
        this.isBankDeposit && hasDepositPhotos && isDepositPhotoRequired
      )
    },
    receiptPhotoLabel() {
      if (!this.isBankDeposit) {
        return 'Foto da nota ou comprovante (opcional)'
      }

      return this.isReceiptPhotoRequired
        ? 'Foto do recibo (obrigatoria)'
        : 'Foto do recibo (opcional)'
    },
    receiptPhotoPlaceholder() {
      return this.isBankDeposit
        ? 'Selecione a imagem do recibo'
        : 'Selecione a foto da nota ou comprovante'
    },
    receiptPhotoAlt() {
      return this.isBankDeposit
        ? 'Recibo do deposito'
        : 'Nota ou comprovante da saida'
    },
    receiptPhotoHelp() {
      return this.isBankDeposit
        ? 'O comprovante pode ser anexado depois na conferencia de depositos.'
        : 'Se houver uma compra, anexe a foto da nota ou do comprovante.'
    },
    categoryOptions() {
      return [
        { text: 'Saida simples', value: OPERATION_CATEGORIES.MANUAL },
        { text: 'Deposito bancario', value: OPERATION_CATEGORIES.BANK_DEPOSIT }
      ]
    },
    nameLabel() {
      return this.isBankDeposit ? 'Descricao opcional' : 'Nome'
    },
    namePlaceholder() {
      return this.isBankDeposit ? 'Ex: deposito do turno da manha' : ''
    },
    validationClass() {
      const { $dirty, $invalid } = this.$v.form.amount

      return {
        'is-invalid': $dirty && $invalid
      }
    },
    receiptValidationClass() {
      const { $dirty, $invalid } = this.$v.form.receiptPhoto

      return {
        'is-invalid': $dirty && $invalid
      }
    },
    receiptPhotoInvalidMessage() {
      if (
        this.isReceiptPhotoRequired &&
        !this.form.receiptPhoto &&
        !this.form.receiptPhotoUrl
      ) {
        return 'A foto do recibo e obrigatoria'
      }

      return 'Se enviar uma foto, selecione uma imagem valida'
    }
  },
  watch: {
    form: {
      handler(value) {
        this.$emit('input', value)
      },
      deep: true
    },
    type(value) {
      if (value !== OPERATION_TYPES.EXPENSE) {
        this.stopReceiptCamera()
        this.clearReceiptPreview()
        this.$set(this.form, 'receiptPhoto', null)
        this.receiptPhotoError = null
      }
    }
  },
  beforeDestroy() {
    this.stopReceiptCamera()
    this.clearReceiptPreview()
  },
  destroyed() {
    this.clearReceiptPreview()
    this.form = {
      name: '',
      type: null,
      category: OPERATION_CATEGORIES.MANUAL,
      amount: 0,
      receiptPhoto: null,
      receiptPhotoUrl: null
    }
  },
  methods: {
    onReceiptPhotoInput(file) {
      if (file) {
        this.setReceiptPhotoFile(file)
      } else {
        this.clearReceiptPreview()
        this.$set(this.form, 'receiptPhoto', null)
      }
    },
    async startReceiptCamera() {
      this.receiptPhotoError = null
      this.stopReceiptCamera()

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.receiptPhotoError =
          'Este dispositivo ou navegador nao permite abrir a camera.'
        return
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' }
          },
          audio: false
        })
        this.receiptCameraStream = stream
        this.receiptCameraActive = true

        await this.$nextTick()
        if (this.$refs.receiptVideo) {
          this.$refs.receiptVideo.srcObject = stream
          await this.$refs.receiptVideo.play()
        }
      } catch (error) {
        this.receiptCameraActive = false
        this.receiptPhotoError = this.getCameraErrorMessage(error)
      }
    },
    captureReceiptPhoto() {
      const video = this.$refs.receiptVideo

      if (!video || !video.videoWidth || !video.videoHeight) {
        this.receiptPhotoError =
          'Nao foi possivel capturar a foto. Aguarde a camera carregar e tente novamente.'
        return
      }

      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const context = canvas.getContext('2d')
      context.drawImage(video, 0, 0, canvas.width, canvas.height)

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            this.receiptPhotoError =
              'Nao foi possivel gerar a imagem do comprovante.'
            return
          }

          const file = new File([blob], `comprovante-saida-${Date.now()}.jpg`, {
            type: 'image/jpeg'
          })
          this.setReceiptPhotoFile(file)
          this.stopReceiptCamera()
        },
        'image/jpeg',
        0.9
      )
    },
    setReceiptPhotoFile(file) {
      this.receiptPhotoError = null

      if (!file.type || !file.type.startsWith('image/')) {
        this.receiptPhotoError = 'Selecione uma imagem valida do comprovante.'
        return
      }

      if (file.size > 8 * 1024 * 1024) {
        this.receiptPhotoError =
          'A foto do comprovante deve ter no maximo 8 MB.'
        return
      }

      this.clearReceiptPreview()
      this.$set(this.form, 'receiptPhoto', file)
      this.receiptPreviewUrl = URL.createObjectURL(file)
    },
    stopReceiptCamera() {
      if (this.receiptCameraStream) {
        this.receiptCameraStream.getTracks().forEach((track) => track.stop())
      }
      this.receiptCameraStream = null
      this.receiptCameraActive = false
    },
    clearReceiptPreview() {
      if (this.receiptPreviewUrl) {
        URL.revokeObjectURL(this.receiptPreviewUrl)
      }
      this.receiptPreviewUrl = null
    },
    getCameraErrorMessage(error) {
      if (error && error.name === 'NotAllowedError') {
        return 'Permita o acesso a camera para fotografar o comprovante.'
      }

      if (error && error.name === 'NotFoundError') {
        return 'Nao encontramos camera disponivel neste dispositivo.'
      }

      if (error && error.name === 'NotReadableError') {
        return 'A camera esta em uso por outro aplicativo. Feche-o e tente novamente.'
      }

      return 'Nao foi possivel abrir a camera. Verifique as permissoes e tente novamente.'
    }
  }
}
</script>

<style scoped>
.receipt-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.receipt-camera,
.receipt-preview {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  overflow: hidden;
}

.receipt-camera-video,
.receipt-preview img {
  display: block;
  max-height: 220px;
  object-fit: contain;
  width: 100%;
}
</style>
